/**
 * 图片裁剪弹窗
 *
 * 功能：
 *  - 拖拽移动裁剪框
 *  - 8 方向 resize 裁剪框
 *  - 三等分辅助线
 *  - 比例锁定：自由 / 原始 / 1:1 / 4:3 / 16:9 / 3:4 / 9:16
 *  - 显示裁剪尺寸和原图尺寸
 *  - 裁剪后创建新图片节点 + 自动连线
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Crop } from 'lucide-react';
import { boardActions, getBoardState } from './state/boardStore';
import { uploadImage, bindUploadToNode } from './imageStorage';
import { makeNode, nid } from './utils';
import type { CanvasNode, ImageNodeData } from './types';
import { loadImage } from '../../utils/imageTransform';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface CropDialogProps {
  imageUrl: string;
  sourceNode: CanvasNode<ImageNodeData>;
  onClose: () => void;
}

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const RATIO_OPTIONS: { label: string; value: number | null }[] = [
  { label: '自由', value: null },
  { label: '原始', value: null }, // 特殊处理
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
];

const CANVAS_SIZE = 600;

export function CropDialog({ imageUrl, sourceNode, onClose }: CropDialogProps) {
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, width: 100, height: 100 });
  const [ratioIndex, setRatioIndex] = useState(0);
  const [originalRatio, setOriginalRatio] = useState(1);
  const dragRef = useRef<{
    type: 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
    startX: number;
    startY: number;
    startCrop: CropRect;
  } | null>(null);

  // 加载图片获取尺寸
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImgSize({ width: img.naturalWidth, height: img.naturalHeight });
      setOriginalRatio(img.naturalWidth / img.naturalHeight);
      // 初始裁剪框：居中 80%
      const w = CANVAS_SIZE * 0.8;
      const h = CANVAS_SIZE * 0.8;
      setCrop({
        x: (CANVAS_SIZE - w) / 2,
        y: (CANVAS_SIZE - h) / 2,
        width: w,
        height: h,
      });
    };
    img.src = resolveBrowserMediaUrl(imageUrl);
  }, [imageUrl]);

  // 缩放比例：将图片映射到 CANVAS_SIZE
  const scale = imgSize.width > 0 ? CANVAS_SIZE / Math.max(imgSize.width, imgSize.height) : 1;
  const displayW = imgSize.width * scale;
  const displayH = imgSize.height * scale;
  const offsetX = (CANVAS_SIZE - displayW) / 2;
  const offsetY = (CANVAS_SIZE - displayH) / 2;

  const getLockedRatio = useCallback(() => {
    if (ratioIndex === 1) return originalRatio; // 原始
    return RATIO_OPTIONS[ratioIndex]?.value ?? null;
  }, [ratioIndex, originalRatio]);

  const onPointerDown = (
    e: React.PointerEvent,
    type: 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      type,
      startX: e.clientX,
      startY: e.clientY,
      startCrop: { ...crop },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const sc = drag.startCrop;
    const ratio = getLockedRatio();
    let next = { ...sc };

    const clampRect = (r: CropRect) => ({
      x: Math.max(0, Math.min(CANVAS_SIZE - r.width, r.x)),
      y: Math.max(0, Math.min(CANVAS_SIZE - r.height, r.y)),
      width: Math.max(20, Math.min(CANVAS_SIZE, r.width)),
      height: Math.max(20, Math.min(CANVAS_SIZE, r.height)),
    });

    switch (drag.type) {
      case 'move':
        next = { ...sc, x: sc.x + dx, y: sc.y + dy };
        break;
      case 'se':
        if (ratio) {
          const newW = sc.width + dx;
          next = { ...sc, width: newW, height: newW / ratio };
        } else {
          next = { ...sc, width: sc.width + dx, height: sc.height + dy };
        }
        break;
      case 'e':
        if (ratio) {
          const newW = sc.width + dx;
          next = { ...sc, width: newW, height: newW / ratio };
        } else {
          next = { ...sc, width: sc.width + dx };
        }
        break;
      case 's':
        if (ratio) {
          const newH = sc.height + dy;
          next = { ...sc, height: newH, width: newH * ratio };
        } else {
          next = { ...sc, height: sc.height + dy };
        }
        break;
      case 'nw':
        if (ratio) {
          const newW = sc.width - dx;
          next = { ...sc, x: sc.x + dx, y: sc.y + (sc.height - newW / ratio), width: newW, height: newW / ratio };
        } else {
          next = { ...sc, x: sc.x + dx, y: sc.y + dy, width: sc.width - dx, height: sc.height - dy };
        }
        break;
      case 'n':
        if (ratio) {
          const newH = sc.height - dy;
          next = { ...sc, y: sc.y + dy, height: newH, width: newH * ratio };
        } else {
          next = { ...sc, y: sc.y + dy, height: sc.height - dy };
        }
        break;
      case 'ne':
        if (ratio) {
          const newW = sc.width + dx;
          next = { ...sc, y: sc.y + (sc.height - newW / ratio), width: newW, height: newW / ratio };
        } else {
          next = { ...sc, y: sc.y + dy, width: sc.width + dx, height: sc.height - dy };
        }
        break;
      case 'sw':
        if (ratio) {
          const newW = sc.width - dx;
          next = { ...sc, x: sc.x + dx, width: newW, height: newW / ratio };
        } else {
          next = { ...sc, x: sc.x + dx, width: sc.width - dx, height: sc.height + dy };
        }
        break;
      case 'w':
        if (ratio) {
          const newW = sc.width - dx;
          next = { ...sc, x: sc.x + dx, width: newW, height: newW / ratio };
        } else {
          next = { ...sc, x: sc.x + dx, width: sc.width - dx };
        }
        break;
    }

    setCrop(clampRect(next));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  // 执行裁剪
  const handleCrop = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 将裁剪框坐标转回原图坐标
    const cropX = ((crop.x - offsetX) / scale);
    const cropY = ((crop.y - offsetY) / scale);
    const cropW = crop.width / scale;
    const cropH = crop.height / scale;

    canvas.width = Math.round(cropW);
    canvas.height = Math.round(cropH);

    try {
      const img = await loadImage(resolveBrowserMediaUrl(imageUrl));
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/png');

      // 派生图远程上传（跟账号走）：新节点先渲染本地预览，上传完成后换持久网关地址
      const blob = await (await fetch(dataUrl)).blob();
      const up = uploadImage(blob, getBoardState().projectId ?? undefined);

      // 创建新图片节点 + 连线
      const newNode = makeNode('image', {
        x: sourceNode.x + sourceNode.width + 60,
        y: sourceNode.y,
      }, {
        imageUrl: up.previewUrl,
        status: 'completed',
        prompt: sourceNode.data.prompt ? `(裁剪) ${sourceNode.data.prompt}` : '(裁剪)',
      } as Record<string, unknown>);
      boardActions.addNode(newNode);
      bindUploadToNode(newNode.id, up);
      boardActions.addEdge({
        id: nid(),
        sourceNodeId: sourceNode.id,
        targetNodeId: newNode.id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });
      onClose();
    } catch (err) {
      console.error('[CropDialog] 裁剪失败', err);
    }
  };

  // 裁剪框在原图上的实际像素
  const realCropW = Math.round(crop.width / scale);
  const realCropH = Math.round(crop.height / scale);

  // 8 方向 resize 手柄
  const handles = [
    { pos: 'nw', style: { left: -4, top: -4, cursor: 'nw-resize' } },
    { pos: 'n', style: { left: '50%', top: -4, marginLeft: -4, cursor: 'n-resize' } },
    { pos: 'ne', style: { right: -4, top: -4, cursor: 'ne-resize' } },
    { pos: 'e', style: { right: -4, top: '50%', marginTop: -4, cursor: 'e-resize' } },
    { pos: 'se', style: { right: -4, bottom: -4, cursor: 'se-resize' } },
    { pos: 's', style: { left: '50%', bottom: -4, marginLeft: -4, cursor: 's-resize' } },
    { pos: 'sw', style: { left: -4, bottom: -4, cursor: 'sw-resize' } },
    { pos: 'w', style: { left: -4, top: '50%', marginTop: -4, cursor: 'w-resize' } },
  ] as const;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex w-[780px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Crop className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-slate-900">裁剪图片</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 裁剪区域 */}
        <div className="flex items-center justify-center bg-slate-100 p-6">
          <div
            className="relative overflow-hidden rounded-lg bg-black/5"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            {/* 图片 */}
            <img
              src={resolveBrowserMediaUrl(imageUrl)}
              alt="裁剪"
              className="absolute select-none"
              style={{
                left: offsetX,
                top: offsetY,
                width: displayW,
                height: displayH,
                pointerEvents: 'none',
              }}
              draggable={false}
            />

            {/* 遮罩层（裁剪框外部变暗） */}
            <svg className="absolute inset-0" width={CANVAS_SIZE} height={CANVAS_SIZE} style={{ pointerEvents: 'none' }}>
              <defs>
                <mask id="crop-mask">
                  <rect x="0" y="0" width={CANVAS_SIZE} height={CANVAS_SIZE} fill="white" />
                  <rect x={crop.x} y={crop.y} width={crop.width} height={crop.height} fill="black" />
                </mask>
              </defs>
              <rect x="0" y="0" width={CANVAS_SIZE} height={CANVAS_SIZE} fill="rgba(0,0,0,0.5)" mask="url(#crop-mask)" />
            </svg>

            {/* 裁剪框 */}
            <div
              className="absolute border-2 border-white shadow-lg"
              style={{
                left: crop.x,
                top: crop.y,
                width: crop.width,
                height: crop.height,
                cursor: 'move',
              }}
              onPointerDown={(e) => onPointerDown(e, 'move')}
            >
              {/* 三等分线 */}
              <div className="absolute left-1/3 top-0 h-full w-px bg-white/40" />
              <div className="absolute left-2/3 top-0 h-full w-px bg-white/40" />
              <div className="absolute left-0 top-1/3 h-px w-full bg-white/40" />
              <div className="absolute left-0 top-2/3 h-px w-full bg-white/40" />

              {/* 8 方向 resize 手柄 */}
              {handles.map((h) => (
                <div
                  key={h.pos}
                  className="absolute h-3 w-3 rounded-full border-2 border-white bg-blue-500"
                  style={{ ...h.style, position: 'absolute', cursor: h.style.cursor } as React.CSSProperties}
                  onPointerDown={(e) => onPointerDown(e, h.pos)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 底部控制栏 */}
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <div className="flex items-center gap-3">
            {/* 比例选择 */}
            <div className="flex items-center gap-1">
              {RATIO_OPTIONS.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRatioIndex(i)}
                  className={`rounded-md px-2 py-1 text-xs transition ${
                    ratioIndex === i
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-slate-400">
              裁剪: {realCropW} x {realCropH} | 原图: {imgSize.width} x {imgSize.height}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleCrop}
              className="flex items-center gap-1.5 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600"
            >
              <Check className="h-4 w-4" />
              裁剪
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
